<template>
  <span ref="root" class="reference-thumbnail">
    <img v-if="displaySrc && !failed" :src="displaySrc" :alt="alt"
      :loading="eager ? 'eager' : 'lazy'" decoding="async"
      :crossorigin="getOssImageCrossorigin(displaySrc)" @load="loaded = true" @error="onError" />
    <video v-if="useVideoFallback && visible" :key="src" :src="src"
      class="reference-first-frame" :style="{ visibility: frameLoaded ? 'inherit' : 'hidden' }"
      preload="metadata" muted playsinline aria-hidden="true"
      @loadedmetadata="seekFirstFrame" @loadeddata="frameLoaded = true" @error="onFrameError" />
    <span v-if="(!loaded || failed) && !frameLoaded" class="reference-thumbnail-placeholder" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path v-if="mediaType.toLowerCase() === 'video'" d="m10 8 6 4-6 4Z" />
        <path v-else d="m3 17 5-5 4 4 4-6 5 7" />
      </svg>
    </span>
  </span>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { getOssImageCrossorigin } from '@/utils/ossImageCache'
import { referenceThumbnailUrl } from '../model/referenceThumbnails'
import { acquireMentionThumbnail, isMentionThumbnailCacheable, peekMentionThumbnail } from '../model/mentionThumbnailCache'

const props = defineProps({ src: { type: String, default: '' }, mediaType: { type: String, default: 'image' },
  alt: { type: String, default: '' }, eager: Boolean, firstFrameFallback: Boolean, compact: Boolean })
const emit = defineEmits(['unavailable'])
const displaySrc = ref(''), loaded = ref(false), failed = ref(false)
const root = ref(null), visible = ref(false), frameLoaded = ref(false)
const thumbnailSource = computed(() => referenceThumbnailUrl(props.src, props.mediaType, { compact: props.compact }))
let thumbnailLease, thumbnailVersion = 0
function loadCachedThumbnail() {
  if (thumbnailLease || !props.compact || !isMentionThumbnailCacheable(thumbnailSource.value)) return
  const version = thumbnailVersion
  thumbnailLease = acquireMentionThumbnail(thumbnailSource.value)
  displaySrc.value = peekMentionThumbnail(thumbnailSource.value)
  thumbnailLease.promise.then(url => {
    if (version === thumbnailVersion) displaySrc.value = url
  }).catch(() => { if (version === thumbnailVersion) failed.value = true })
}
const useVideoFallback = computed(() => props.firstFrameFallback && props.mediaType.toLowerCase() === 'video' &&
  props.src && (!displaySrc.value || failed.value))
let observer
onMounted(() => {
  if (!props.firstFrameFallback && !props.compact) return
  if (typeof IntersectionObserver === 'undefined') { visible.value = true; return }
  observer = new IntersectionObserver(([entry]) => {
    // Once loaded, retain the paused frame across hover and scrolling.
    if (entry.isIntersecting) { visible.value = true; observer.disconnect() }
  })
  observer.observe(root.value)
})
onBeforeUnmount(() => { observer?.disconnect(); thumbnailVersion++; thumbnailLease?.release() })
watch(() => [thumbnailSource.value, props.compact], () => {
  thumbnailVersion++
  thumbnailLease?.release()
  thumbnailLease = null
  const cached = props.compact && isMentionThumbnailCacheable(thumbnailSource.value)
  displaySrc.value = cached ? '' : thumbnailSource.value
  loaded.value = false
  failed.value = false
  frameLoaded.value = false
  if (cached && (visible.value || peekMentionThumbnail(thumbnailSource.value))) loadCachedThumbnail()
}, { immediate: true })
watch(visible, value => { if (value) loadCachedThumbnail() })
function seekFirstFrame(event) {
  // A tiny seek makes browsers that defer painting at time zero decode the opening frame.
  const video = event.target
  if (Number.isFinite(video.duration) && video.duration > 0) video.currentTime = Math.min(0.001, video.duration / 2)
}
function onError() {
  // Small mention thumbnails must not retry with a potentially large original.
  if (props.compact) { failed.value = true; return }
  // Video fallback is opt-in for result cards; reference pickers remain image-only.
  if (props.mediaType.toLowerCase() === 'image' && displaySrc.value !== props.src) displaySrc.value = props.src
  else failed.value = true
}
function onFrameError() { frameLoaded.value = false; emit('unavailable', props.src) }
</script>

<style scoped>
.reference-thumbnail { position: relative; display: block; width: 100%; height: 100%; overflow: hidden; background: #edf3fc; }
.reference-thumbnail img { display: block; width: 100%; height: 100%; object-fit: contain; }
.reference-first-frame { display: block; width: 100%; height: 100%; object-fit: contain; pointer-events: none; }
.reference-thumbnail-placeholder { position: absolute; inset: 0; display: grid; place-items: center; color: #8a9ab4; }
.reference-thumbnail-placeholder svg { width: 45%; height: 45%; max-width: 36px; max-height: 36px; }
</style>
